import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import Select from "react-select"
import axios, { AxiosError, AxiosResponse } from "axios"
import { FormikProps } from "formik/dist/types"
import clsx from "clsx"
import { ObjectSchema } from "yup"
import useSelectReact, { SelectOptionsType, SelectOptionType, SelectOptionValueType } from "./useSelectReact"
import useFormik from "./useFormik"
import { debounce } from "lodash"

const MySelectSearchDataFromServerFormField = ({
     name,
     placeholder,
     payload = {},
     isClearable = true,
     isMulti = false,
     minimumLengthSearch,
     defaultOptions = [],
     method,
     label,
     autoFocus,
     formikProps,
     classNameLayout,
     classNameLabel,
     classNameField,
     endpoint,
     validationSchema,
     onChangeCallback = () => {},
}: IPropsModel) => {
     const { formatGroupLabel, styles } = useSelectReact()
     const { isFieldValid, isFieldOnError, getFieldErrorMessage, isFieldRequired } = useFormik()

     const [isLoading, setIsLoading] = useState<boolean>(false)
     const [options, setOptions] = useState<SelectOptionsType>(defaultOptions)
     const [cacheOptions, setCacheOptions] = useState<SelectOptionsType>(defaultOptions)
     const [noOptionsMessage, setNoOptionsMessage] = useState<string>(`Saisissez au moins ${minimumLengthSearch} caractères pour lancer la recherche`)

     // 🔹 Debounced API Call using lodash
     // We use useMemo to ensure the debounced function is stable across renders
     const fetchOptions = useMemo(() => {
          return debounce((q: string) => {
               if (q.length < minimumLengthSearch) {
                    setOptions([])
                    return
               }

               setIsLoading(true)
               // Note: We don't set "Loading..." message here because it might flicker.
               // It's handled immediately in handleInputChange.

               let result
               // Adjust payload values if necessary.
               if (method === "GET") {
                    const params = { q, ...payload }
                    for (const key in payload) {
                         if (payload[key] === true) params[key] = 1
                         if (payload[key] === false) params[key] = 0
                    }

                    result = axios.get<Array<{ label: string; value: string | number }>>(endpoint, {
                         params: params,
                    })
               } else {
                    result = axios.post<Array<{ label: string; value: string | number }>>(endpoint, { q, ...payload })
               }

               result
                    .then((r: AxiosResponse) => {
                         setOptions(r.data)
                         setCacheOptions(prev => {
                              // Logic to merge new options into cache without duplicates
                              const newCache = [...prev]
                              r.data.forEach(item => {
                                   if ("options" in item) {
                                        item.options.forEach(subItem => {
                                             if (!newCache.some(e => e.value === subItem.value)) {
                                                  newCache.push(subItem)
                                             }
                                        })
                                   } else {
                                        if (!newCache.some(e => e.value === item.value)) {
                                             newCache.push(item)
                                        }
                                   }
                              })
                              return newCache
                         })
                    })
                    .catch((e: AxiosError) => {
                         setNoOptionsMessage(e.response?.status === 400 ? e.response.data.detail : "Une erreur est survenue")
                         setOptions([])
                    })
                    .finally(() => {
                         setIsLoading(false)
                         // Reset message if empty results, but usually handled by 'noOptionsMessage' prop logic or subsequent logic
                         if (options.length === 0) setNoOptionsMessage("Aucun résultat")
                    })
          }, 700)
     }, [endpoint, method, payload, minimumLengthSearch])

     // 🔹 FIX 1: Hydration Effect (Runs on page reload/mount)
     useEffect(() => {
          const initVal = formikProps?.getFieldProps(name).value

          // 1. Check if there is a value to hydrate
          if (initVal === undefined || initVal === null || initVal === "" || (Array.isArray(initVal) && initVal.length === 0)) {
               return
          }

          // 2. Check if we already have these options in cache
          const isCached = Array.isArray(initVal) ? initVal.every((val: any) => cacheOptions.find(opt => opt.value === val)) : cacheOptions.find(opt => opt.value === initVal)

          if (isCached) return

          setIsLoading(true)

          // 3. Prepare params
          // 🔴 KEY FIX: Added 'q: ""' because your backend requires 'q' to be present
          const params: any = { ...payload, q: "" }
          params.id = initVal

          if (method === "GET") {
               for (const key in payload) {
                    if (params[key] === true) params[key] = 1
                    if (params[key] === false) params[key] = 0
               }
          }

          const promise = method === "GET" ? axios.get<SelectOptionsType>(endpoint, { params }) : axios.post<SelectOptionsType>(endpoint, params)

          promise
               .then(r => {
                    setCacheOptions(prev => {
                         const newOptions = [...prev]
                         r.data.forEach(item => {
                              if ("options" in item) {
                                   item.options.forEach(subItem => {
                                        if (!newOptions.some(e => e.value === subItem.value)) {
                                             newOptions.push(subItem)
                                        }
                                   })
                              } else {
                                   if (!newOptions.some(e => e.value === item.value)) {
                                        newOptions.push(item)
                                   }
                              }
                         })
                         return newOptions
                    })
               })
               .catch(e => {
                    console.error("Error hydrating initial values for " + name, e)
               })
               .finally(() => setIsLoading(false))
     }, [])

     function onChange(val: any) {
          if (val === null) {
               formikProps?.setFieldValue(name, null)
          } else {
               if (Array.isArray(val)) {
                    formikProps?.setFieldValue(
                         name,
                         val.map(i => i.value)
                    )
                    onChangeCallback(
                         val.map(i => i.value),
                         val.map(i => i)
                    )
               } else {
                    formikProps?.setFieldValue(name, val?.value)
                    onChangeCallback(val?.value, val)
               }
          }
     }

     // 🔹 FIX 2: Removed redundant setTimeout. lodash.debounce handles the delay.
     const handleInputChange = (q: string) => {
          if (q.length < minimumLengthSearch) {
               setNoOptionsMessage(`Saisissez au moins ${minimumLengthSearch - q.length} caractères pour lancer la recherche`)
          } else {
               setNoOptionsMessage("Chargement en cours ...")
               // Directly call the debounced function.
               // It will wait 700ms before executing the API call logic.
               fetchOptions(q)
          }
     }

     const selectValue = useCallback(() => {
          const val = formikProps?.getFieldProps(name).value
          if (!val) return isMulti ? [] : null // Return appropriate empty state

          if (Array.isArray(val)) {
               return cacheOptions.filter(opt => ("options" in opt ? opt.options.some(item => val.includes(item.value)) : val.includes(opt.value)))
          } else {
               return cacheOptions.find(opt => opt.value === val) || null
          }
     }, [formikProps?.getFieldProps(name).value, cacheOptions, isMulti])

     return (
          <div className={clsx("row", classNameLayout)}>
               <div className={clsx("col-12 d-flex align-items-center", classNameLabel)}>
                    {label && (
                         <label
                              className={clsx("fs-6 fw-bolder text-gray-900", {
                                   required: validationSchema && isFieldRequired(validationSchema, name),
                              })}
                         >
                              {label}
                         </label>
                    )}
               </div>
               <div
                    className={clsx("col-12", classNameField, {
                         "mt-1": label,
                    })}
               >
                    <Select
                         menuPortalTarget={document.body}
                         isClearable={isClearable}
                         placeholder={placeholder}
                         name={name}
                         formatGroupLabel={formatGroupLabel}
                         styles={styles(!!(formikProps && isFieldOnError(formikProps, name)), !!(formikProps && isFieldValid(formikProps, name)))}
                         value={selectValue()}
                         options={options}
                         loadingMessage={() => "Chargement en cours ..."}
                         onInputChange={handleInputChange}
                         onChange={onChange}
                         isLoading={isLoading}
                         isMulti={isMulti}
                         noOptionsMessage={() => noOptionsMessage}
                         onMenuClose={() => formikProps?.getFieldHelpers(name).setTouched(true)}
                         autoFocus={autoFocus}
                    />
                    {formikProps && isFieldOnError(formikProps, name) && <div className="text-danger">{getFieldErrorMessage(formikProps, name)}</div>}
               </div>
          </div>
     )
}

interface IPropsModel {
     label?: ReactNode
     name: string
     formikProps: FormikProps<any> | null
     endpoint: string
     method: "POST" | "GET"
     placeholder: string
     minimumLengthSearch: number
     isClearable?: boolean
     payload?: Record<string, any>
     isMulti?: boolean
     defaultOptions?: SelectOptionsType
     classNameLayout?: string
     classNameLabel?: string
     classNameField?: string
     autoFocus?: boolean
     validationSchema?: ObjectSchema<any>
     onChangeCallback?: (val: SelectOptionValueType | Array<SelectOptionValueType>, option: SelectOptionType | Array<SelectOptionType>) => void
}

export default MySelectSearchDataFromServerFormField
